import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Tag, Button, Space, Spin, Alert, Drawer, Descriptions, Popconfirm } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconUserCog, IconDownload } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import {
  getAdminOperators,
  getAdminOperator,
  suspendAdminOperator,
  reinstateAdminOperator,
  downloadAdminOperatorDocument,
  IAdminOperatorSummary,
  IAdminOperatorDetail,
  IAdminOperatorDocument,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { useAppBreakpoint } from '../../../hooks/useAppBreakpoint';
import { useTaxonomy } from '../../../hooks/useTaxonomy';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, numberSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { Search } = Input;

const STATE_OPTIONS = ['offerable', 'suspended', 'inactive', 'unlinked'];

const STATE_LABEL_KEYS: Record<string, string> = {
  offerable: TRANSLATIONS.ADMIN_OPERATORS_STATE_OFFERABLE,
  suspended: TRANSLATIONS.ADMIN_OPERATORS_STATE_SUSPENDED,
  inactive: TRANSLATIONS.ADMIN_OPERATORS_STATE_INACTIVE,
  unlinked: TRANSLATIONS.ADMIN_OPERATORS_STATE_UNLINKED,
};

/**
 * The operator register.
 *
 * Deliberately NOT a verification queue: there is no approve and no reject
 * here, because the platform is not an authority on driving licences or PRL
 * training and doesn't claim to be (see AdminOperatorsController's own doc
 * comment). Whoever puts an operator on the roster is the one asserting
 * their credentials are real. The only verdict available is commercial --
 * refusing to carry somebody -- and it always carries a reason, because the
 * host is told that reason verbatim.
 *
 * The state a reviewer actually cares about is "can this person be booked
 * right now", which is not the same as "is their host's switch on": the
 * server computes it (isOfferable) so this screen can never disagree with
 * the booking path about who is bookable.
 */
const OperatorsSection: React.FC = () => {
  const { t } = useTranslation();
  const { isMobile } = useAppBreakpoint();
  const { resolveName: categoryLabel } = useTaxonomy();
  const [operators, setOperators] = useState<IAdminOperatorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [state, setState] = useState<string | undefined>(undefined);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [detail, setDetail] = useState<IAdminOperatorDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [suspendTargetId, setSuspendTargetId] = useState<string | undefined>(undefined);
  const [downloadingId, setDownloadingId] = useState<string | undefined>(undefined);

  const loadOperators = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminOperators(state, search)
      .then(setOperators)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [state, search]);

  useEffect(() => {
    loadOperators();
  }, [loadOperators]);

  const openDetail = (id: string) => {
    setDetailLoading(true);
    setDetail({} as IAdminOperatorDetail);
    getAdminOperator(id)
      .then(setDetail)
      .catch(() => {
        message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
        setDetail(null);
      })
      .finally(() => setDetailLoading(false));
  };

  const handleSuspend = async (reason: string) => {
    if (!suspendTargetId) return;
    try {
      await suspendAdminOperator(suspendTargetId, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setSuspendTargetId(undefined);
      setDetail(null);
      loadOperators();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReinstate = async (id: string) => {
    try {
      await reinstateAdminOperator(id);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setDetail(null);
      loadOperators();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleDownload = async (operatorId: string, doc: IAdminOperatorDocument) => {
    setDownloadingId(doc.id);
    try {
      await downloadAdminOperatorDocument(operatorId, doc);
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_OPERATORS_DOWNLOAD_ERROR));
    } finally {
      setDownloadingId(undefined);
    }
  };

  // One badge, answering the only question worth asking at a glance: can
  // anybody book this person right now, and if not, whose doing is it.
  const renderState = (record: IAdminOperatorSummary) => {
    if (record.suspendedAt) {
      return <Tag color="red" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_OPERATORS_STATE_SUSPENDED)}</Tag>;
    }
    if (!record.isActive) {
      return <Tag style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_OPERATORS_STATE_INACTIVE)}</Tag>;
    }
    if (record.linkedMachineryCount === 0) {
      return <Tag color="orange" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_OPERATORS_STATE_UNLINKED)}</Tag>;
    }
    return <Tag color="green" style={{ marginInlineEnd: 0 }}>{translate(t, TRANSLATIONS.ADMIN_OPERATORS_STATE_OFFERABLE)}</Tag>;
  };

  const renderCategories = (value: string) => (
    <Space size={4} wrap>
      {value
        .split(',')
        .filter(Boolean)
        .map((code) => (
          <Tag key={code} style={{ borderRadius: 999, marginInlineEnd: 0 }}>
            {categoryLabel(code)}
          </Tag>
        ))}
    </Space>
  );

  // Shared by the desktop "Acciones" column and the mobile card footer.
  const renderActions = (record: IAdminOperatorSummary) => (
    <Space wrap>
      <Button size="small" onClick={() => openDetail(record.id)}>
        {translate(t, TRANSLATIONS.ADMIN_OPERATORS_VIEW_BUTTON)}
      </Button>
      {record.suspendedAt ? (
        <Popconfirm
          title={translate(t, TRANSLATIONS.ADMIN_OPERATORS_REINSTATE_CONFIRM)}
          onConfirm={() => handleReinstate(record.id)}
        >
          <Button size="small">{translate(t, TRANSLATIONS.ADMIN_OPERATORS_REINSTATE_BUTTON)}</Button>
        </Popconfirm>
      ) : (
        <Button size="small" danger onClick={() => setSuspendTargetId(record.id)}>
          {translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUSPEND_BUTTON)}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_OPERATORS)}
      </Title>
      {/* Says out loud what this screen is NOT, so nobody arriving here later
          mistakes it for a licensing desk and starts "approving" people. */}
      <Text type="secondary" style={{ display: 'block', marginBottom: 16, maxWidth: 720 }}>
        {translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUBTITLE)}
      </Text>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_OPERATORS_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
        <Select
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_OPERATORS_STATE_FILTER_ALL)}
          style={{ maxWidth: 240, width: '100%' }}
          value={state}
          onChange={setState}
          options={STATE_OPTIONS.map((value) => ({ value, label: translate(t, STATE_LABEL_KEYS[value]) }))}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_OPERATORS_LOAD_ERROR)} />
      ) : operators.length === 0 ? (
        <EmptyState
          icon={<IconUserCog size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_OPERATORS_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_OPERATORS_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminOperatorSummary>
          rowKey="id"
          dataSource={operators}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.id,
            title: record.fullName,
            subtitle: record.hostName,
            badge: renderState(record),
            fields: [
              { label: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_MACHINES), value: record.linkedMachineryCount },
              { label: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_DOCS), value: record.documentCount },
              {
                label: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_CATEGORIES),
                value: renderCategories(record.qualifiedCategories),
                wide: true,
              },
              {
                label: translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_SUSPENSION_REASON),
                value: record.suspensionReason,
                wide: true,
              },
            ],
            actions: renderActions(record),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_NAME),
              dataIndex: 'fullName',
              key: 'fullName',
              sorter: textSorter<IAdminOperatorSummary>('fullName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_HOST),
              dataIndex: 'hostName',
              key: 'hostName',
              sorter: textSorter<IAdminOperatorSummary>('hostName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_CATEGORIES),
              dataIndex: 'qualifiedCategories',
              key: 'qualifiedCategories',
              render: (value: string) => renderCategories(value),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_STATE),
              key: 'state',
              render: (_: unknown, record: IAdminOperatorSummary) => renderState(record),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_MACHINES),
              dataIndex: 'linkedMachineryCount',
              key: 'linkedMachineryCount',
              sorter: numberSorter<IAdminOperatorSummary>('linkedMachineryCount'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_DOCS),
              dataIndex: 'documentCount',
              key: 'documentCount',
              sorter: numberSorter<IAdminOperatorSummary>('documentCount'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_CREATED),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminOperatorSummary>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_ACTIONS),
              key: 'actions',
              render: (_: unknown, record: IAdminOperatorSummary) => renderActions(record),
            },
          ]}
        />
      )}

      <Drawer
        title={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_TITLE)}
        open={detail !== null}
        onClose={() => setDetail(null)}
        width={isMobile ? '100%' : 480}
      >
        {detailLoading || !detail?.id ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : (
          <>
            <Descriptions column={1} bordered size="small" style={{ marginBottom: 20 }}>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_NAME)}>{detail.fullName}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_HOST)}>{detail.hostName}</Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_EMAIL)}>
                <a href={`mailto:${detail.email}`}>{detail.email}</a>
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_PHONE)}>
                {detail.phone ? <a href={`tel:${detail.phone}`}>{detail.phone}</a> : '—'}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_RATE)}>
                {detail.hourlyRate.toFixed(2)} €/h
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_CATEGORIES)}>
                {renderCategories(detail.qualifiedCategories)}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_COL_STATE)}>{renderState(detail)}</Descriptions.Item>
              {detail.suspendedAt && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_SUSPENDED_AT)}>
                  {new Date(detail.suspendedAt).toLocaleString()}
                </Descriptions.Item>
              )}
              {detail.suspensionReason && (
                <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_SUSPENSION_REASON)}>
                  {detail.suspensionReason}
                </Descriptions.Item>
              )}
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_COMPLETED)}>
                {detail.completedBookingCount}
              </Descriptions.Item>
              <Descriptions.Item label={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_UPCOMING)}>
                {detail.upcomingBookingCount}
              </Descriptions.Item>
            </Descriptions>

            <Title level={5} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_LINKED)}
            </Title>
            {detail.linkedMachineryTitles.length === 0 ? (
              <Text type="secondary" style={{ display: 'block', marginBottom: 20 }}>
                {translate(t, TRANSLATIONS.ADMIN_OPERATORS_DETAIL_LINKED_NONE)}
              </Text>
            ) : (
              <Space size={6} wrap style={{ marginBottom: 20 }}>
                {detail.linkedMachineryTitles.map((title) => (
                  <Tag key={title} style={{ borderRadius: 999, marginInlineEnd: 0 }}>
                    {title}
                  </Tag>
                ))}
              </Space>
            )}

            <Title level={5} style={{ marginBottom: 8 }}>
              {translate(t, TRANSLATIONS.ADMIN_OPERATORS_DOCUMENTS_TITLE)}
            </Title>
            {/* The warning is the point of this block, not decoration: these
                are licences and PRL certificates, nobody reviews them, and
                every download is logged. */}
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={translate(t, TRANSLATIONS.ADMIN_OPERATORS_DOCUMENTS_WARNING)}
            />
            {detail.documents.length === 0 ? (
              <Text type="secondary">{translate(t, TRANSLATIONS.ADMIN_OPERATORS_DOCUMENTS_EMPTY)}</Text>
            ) : (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {detail.documents.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <Text strong style={{ display: 'block', fontSize: 13 }}>
                        {doc.documentType}
                      </Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {new Date(doc.uploadedAt).toLocaleDateString()}
                      </Text>
                    </div>
                    <Button
                      size="small"
                      icon={<IconDownload size={14} />}
                      loading={downloadingId === doc.id}
                      onClick={() => handleDownload(detail.id, doc)}
                    />
                  </div>
                ))}
              </Space>
            )}
          </>
        )}
      </Drawer>

      <AdminReasonModal
        open={suspendTargetId !== undefined}
        title={translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUSPEND_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_OPERATORS_SUSPEND_BUTTON)}
        onCancel={() => setSuspendTargetId(undefined)}
        onConfirm={handleSuspend}
      />
    </div>
  );
};

export default OperatorsSection;
